// SIL-548 (W3, Wayfinder UX-consistency spec §6): three-state theme control —
// System / Light / Dark, defaulting to System. Mirrors the resolution logic
// baked into the index.html pre-paint script (kept in sync by hand — the
// pre-paint script can't import this module, it has to run before any JS
// bundle loads to avoid a flash of the wrong theme).
//
// Storage contract: 'siliconx-theme' holds 'light' | 'dark' when the user has
// explicitly pinned a theme. Absence of the key (or any other value) means
// System — resolved live from `prefers-color-scheme`, never written to
// storage. Existing users who already have 'dark'/'light' stored stay pinned
// on upgrade; System is only ever the default for a session that has never
// set the key.
export type ThemeChoice = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'siliconx-theme';

/** Reads the current pin from storage, defaulting to 'system' when unset or invalid. */
export function getStoredThemeChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

/** Live OS preference — call fresh each time, do not cache the result. */
export function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Resolves a choice to a boolean dark/light, consulting the OS for 'system'. */
export function resolveIsDark(choice: ThemeChoice): boolean {
  return choice === 'system' ? prefersDark() : choice === 'dark';
}

/**
 * Persists `choice` (System clears the pin rather than writing 'system') and
 * applies the resulting dark/light class to <html> immediately.
 */
export function applyThemeChoice(choice: ThemeChoice): void {
  if (typeof window === 'undefined') return;
  if (choice === 'system') {
    window.localStorage.removeItem(THEME_STORAGE_KEY);
  } else {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  }
  document.documentElement.classList.toggle('dark', resolveIsDark(choice));
}
